import { consoleLogger } from '@proc7ts/logger';
import { lazyValue, noop } from '@proc7ts/primitives';
import { HttpAddressRep } from 'http-header-value/node.js';
import { dispatchError } from '../core/dispatch-error.js';
import { Logging } from '../core/logging/logging.capability.js';
import { requestProcessor } from '../core/request-processor.js';
import { HttpError } from './http-error.js';
import { renderHttpError } from './render/render-http-error.handler.js';
export function httpListener(configOrHandler, optionalHandler) {
    let config;
    let handler;
    if (optionalHandler) {
        config = configOrHandler;
        handler = optionalHandler;
    }
    else {
        config = {};
        handler = configOrHandler;
    }
    const fullHandler = fullHttpHandler(config, handler);
    const incomingHandler = incomingHttpHandler(config.handleBy
        ? config.handleBy(fullHandler)
        : fullHandler);
    const processor = requestProcessor({
        handler: incomingHandler,
        next(handler, context) {
            const { response } = context;
            return Promise.resolve(context)
                .then(handler)
                .then(() => response.writableEnded);
        },
    });
    return (request, response) => {
        new Promise((onResponse, onError) => {
            processor({
                request,
                response,
                onResponse,
                onError,
            }).then(onResponse, onError);
        }).catch(error => {
            consoleLogger.error(`[${request.method} ${request.url}]`, 'Unhandled error', error);
        });
    };
}
/**
 * @internal
 */
function incomingHttpHandler(handler) {
    return ({ request, response, next, onResponse, onError }) => {
        response.once('error', onError);
        response.once('finish', onResponse);
        response.once('close', onResponse);
        const end = response.end.bind(response);
        // Finish request processing immediately after calling `response.end()`
        response.end = ((chunk, encoding, cb) => {
            end(chunk, encoding, cb);
            onResponse(true);
        });
        const requestDefaults = lazyValue(() => HttpAddressRep.defaults(request));
        const requestURL = lazyValue(() => {
            const { host, proto } = requestDefaults();
            const { url = '' } = request;
            return new URL(url, `${proto}://${host}`);
        });
        return next(handler, {
            requestAddresses: {
                get ip() {
                    return requestDefaults().for;
                },
                get url() {
                    return requestURL();
                },
            },
        });
    };
}
/**
 * @internal
 */
function fullHttpHandler(config, handler) {
    const defaultHandler = defaultHttpHandler(config);
    return dispatchError(httpErrorHandler(config), ({ next }) => next(handler).then(ok => ok || next(defaultHandler)));
}
/**
 * @internal
 */
function defaultHttpHandler({ defaultHandler = true, }) {
    return defaultHandler
        ? defaultHandler !== true
            ? defaultHandler
            : () => Promise.reject(new HttpError(404))
        : noop;
}
/**
 * @internal
 */
function httpErrorHandler({ logError = true, defaultHandler = true, errorHandler = true, }) {
    const onError = errorHandler
        ? errorHandler === true
            ? renderHttpError
            : errorHandler
        : defaultHandler
            ? renderEmptyHttpResponse
            : noop;
    if (!logError) {
        return onError;
    }
    return Logging.for(context => {
        logHttpError(context);
        return context.next(onError);
    });
}
/**
 * @internal
 */
function logHttpError({ log, error }) {
    log.error(error);
}
/**
 * @internal
 */
function renderEmptyHttpResponse(context) {
    context.response.end();
}
//# sourceMappingURL=http-listener.js.map